/**
 * State Flow Visual Component
 * 
 * Renders a horizontal state flow with connecting arrows and color highlighting
 * based on state actions.
 */

import React from 'react';
import { Card, Tag, Space, Typography } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { StateAction, normalizeStateAction } from '../../types/globalProperties';
import {
  StandardOperationalState,
  StandardImprovementState,
  getStateInfo
} from '../../types/standardStates';
import styles from './StateFlowVisual.module.css';

const { Text } = Typography;

interface StateActionInfo {
  forward?: StateAction | string;
  backward?: StateAction | string;
  both?: StateAction | string;
}

interface StateFlowVisualProps {
  states: string[];
  stateActions: Record<string, StateAction>;
  stateActionDetails?: Record<string, StateActionInfo>; // Optional detailed info for forward/backward
  flowType: 'operational' | 'improvement';
  onActionChange?: (state: string, action: StateAction) => void;
  onStateClick?: (state: string, flowType: 'operational' | 'improvement') => void;
  editable?: boolean;
}

// State action colors (4-type model: System, User, Calculate, Reference)
const ACTION_COLORS: Partial<Record<StateAction, string>> = {
  none: '#f0f0f0', // Light gray
  System: '#1890ff', // Blue
  User: '#52c41a', // Green
  Calculate: '#13c2c2', // Cyan
  Reference: '#722ed1', // Purple
};

const ACTION_LABELS: Partial<Record<StateAction, string>> = {
  none: 'None',
  System: 'System',
  User: 'User',
  Calculate: 'Calculate',
  Reference: 'Reference',
};

export const StateFlowVisual: React.FC<StateFlowVisualProps> = ({
  states,
  stateActions,
  stateActionDetails,
  flowType,
  onActionChange,
  onStateClick,
  editable = false
}) => {
  const getActionColor = (action: StateAction): string => {
    // Try direct lookup first, then normalize and try again, then default
    return ACTION_COLORS[action] || ACTION_COLORS[normalizeStateAction(action)] || ACTION_COLORS.none || '#d9d9d9';
  };
  
  const getActionLabel = (action: StateAction): string => {
    // Try direct lookup first, then normalize and try again, then use action string
    return ACTION_LABELS[action] || ACTION_LABELS[normalizeStateAction(action)] || String(action);
  };

  const getStateAction = (state: string): StateAction => {
    return stateActions[state] || 'none';
  };

  const getStateActionDetails = (state: string): StateActionInfo | undefined => {
    return stateActionDetails?.[state];
  };

  const hasAction = (state: string): boolean => {
    const action = getStateAction(state);
    const details = getStateActionDetails(state);
    return action !== 'none' || !!(details?.forward || details?.backward || details?.both);
  };

  return (
    <div className={styles.stateFlowVisual}>
      <div className={styles.stateFlowContainer}>
        {states.map((state, index) => {
          const action = getStateAction(state);
          const details = getStateActionDetails(state);
          const stateInfo = getStateInfo(state);
          const isHighlighted = hasAction(state);
          
          // Determine if we have forward and backward separately
          const hasForward = !!(details?.forward);
          const hasBackward = !!(details?.backward);
          const hasBoth = !!(details?.both) || (action !== 'none' && !hasForward && !hasBackward);
          
          // Determine primary action for background color
          const primaryAction: StateAction = (details?.both || details?.forward || details?.backward || action) as StateAction;
          const forwardAction: StateAction | undefined = details?.forward as StateAction | undefined;
          const backwardAction: StateAction | undefined = details?.backward as StateAction | undefined;
          
          return (
            <React.Fragment key={state}>
              <div className={styles.stateFlowItem}>
                <Card
                  className={`${styles.stateCard} ${isHighlighted ? styles.stateHighlighted : ''}`}
                  style={{
                    backgroundColor: isHighlighted ? getActionColor(primaryAction) : undefined,
                    borderColor: isHighlighted ? getActionColor(primaryAction) : undefined,
                    minWidth: '120px',
                    textAlign: 'center',
                    cursor: editable && onStateClick ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (editable && onStateClick) {
                      onStateClick(state, flowType);
                    }
                  }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text strong style={{ 
                      color: isHighlighted ? '#fff' : undefined,
                      fontSize: '14px'
                    }}>
                      {state}
                    </Text>
                    {isHighlighted && (
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {hasBoth && (
                          <Tag 
                            color={primaryAction === 'none' ? 'default' : undefined}
                            style={{
                              backgroundColor: primaryAction !== 'none' ? 'rgba(255, 255, 255, 0.3)' : undefined,
                              color: isHighlighted ? '#fff' : undefined,
                              border: 'none',
                              margin: 0,
                              fontSize: '11px'
                            }}
                          >
                            {getActionLabel(primaryAction)}
                          </Tag>
                        )}
                        {hasForward && forwardAction && (
                          <Tag 
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.3)',
                              color: isHighlighted ? '#fff' : undefined,
                              border: 'none',
                              margin: 0,
                              fontSize: '10px'
                            }}
                          >
                            ↑ {getActionLabel(forwardAction)}
                          </Tag>
                        )}
                        {hasBackward && backwardAction && (
                          <Tag 
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.3)',
                              color: isHighlighted ? '#fff' : undefined,
                              border: 'none',
                              margin: 0,
                              fontSize: '10px'
                            }}
                          >
                            ↓ {getActionLabel(backwardAction)}
                          </Tag>
                        )}
                      </Space>
                    )}
                    {!isHighlighted && editable && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        None
                      </Text>
                    )}
                  </Space>
                </Card>
                {editable && onActionChange && !hasForward && !hasBackward && (
                  <div className={styles.stateActionIndicator}>
                    <Tag 
                      color={action === 'none' ? 'default' : undefined}
                      style={{
                        backgroundColor: action !== 'none' ? getActionColor(action) : undefined,
                        color: action !== 'none' ? '#fff' : undefined,
                        marginTop: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      {getActionLabel(action)}
                    </Tag>
                  </div>
                )}
              </div>
              {index < states.length - 1 && (
                <div className={styles.stateFlowArrow}>
                  <ArrowRightOutlined 
                    style={{ 
                      fontSize: '20px', 
                      color: '#8c8c8c',
                      margin: '0 8px'
                    }} 
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

